import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Phone, CalendarClock, CheckCircle2, Clock, AlertTriangle,
  Loader2, RefreshCw, ExternalLink, Trash2, Users2,
  BarChart3, ShieldCheck, PhoneCall,
} from 'lucide-react';
import { toast } from 'sonner';
import { AddCallToCrmModal } from '../components/AddCallToCrmModal';
import { ScheduleCallModal } from '../components/ScheduleCallModal';
import AppHeader from '../components/AppHeader';
import { EightByEightCallsList, EIGHT_X_EIGHT_LIME, EIGHT_X_EIGHT_DARK } from '../components/EightXEightCallsBar';
import { useZohoSession, isZohoGateEnabled } from '../contexts/ZohoSessionContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOURS = {
  completed: '#10b981',
  scheduled: '#8b5cf6',
  overdue: '#f59e0b',
};

const _fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

/**
 * Manage Calls page — "My Calls" (the current user's Scheduled / 8x8 /
 * Completed calls) and, for CRM managers, "My Team" (the same view for any
 * member of a team they manage, one at a time via member tabs — same
 * pattern as the Sales Hub's My Hub / Team Hub switcher).
 *
 * The old Team Overview (uploaded-report analytics) and Market Sector tabs
 * have been archived — their components (`MarketSectorView`,
 * `TeamOverviewReportModal`, etc.) still exist and are used elsewhere, they
 * are just no longer wired into this page.
 */
export default function ManageCallsPage({ user, onLogout }) {
  const [tab, setTab] = useState('mine');  // 'mine' | 'team'
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamRefreshTick, setTeamRefreshTick] = useState(0);

  const [activeCall, setActiveCall] = useState(null);       // Complete-scheduled (Today's scheduled)
  const [editingCall, setEditingCall] = useState(null);      // Edit-in-place (any other status)
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const isManager = !!user?.is_crm_manager || (user?.role || '').toLowerCase() === 'super_admin';

  const { hasConfirmedZoho, isLinked, openPrompt, loading: zohoLoading } = useZohoSession();
  // Effective "session confirmed" — when the gate feature is disabled
  // (Settings → Integrations → Zoho login check OFF, the default), every
  // logged-in user with backend-side CRM access is implicitly confirmed.
  const gateOn = isZohoGateEnabled();
  const sessionReady = !gateOn || hasConfirmedZoho;

  const fetchMine = useCallback(async () => {
    // Don't fire Zoho-backed fetches until the user has confirmed their CRM
    // session for this browser (only relevant when the gate feature is on) —
    // otherwise we burn API calls the moment they land on /manage-calls
    // without ever intending to use CRM features.
    if (!sessionReady) return;
    setLoading(true);
    try {
      const res = await api.get(`${API}/zoho/my-calls/summary`);
      setSummary(res.data);
    } catch (_) {
      toast.error('Failed to load your calls');
    } finally {
      setLoading(false);
    }
  }, [sessionReady]);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  // If the user arrives here without a confirmed Zoho session AND the gate
  // is enabled in Settings, open the prompt. Default behaviour (gate off):
  // do nothing — calls just load.
  useEffect(() => {
    if (gateOn && !zohoLoading && !hasConfirmedZoho) openPrompt();
  }, [gateOn, zohoLoading, hasConfirmedZoho, openPrompt]);

  const handleDelete = async (callId) => {
    if (!window.confirm('Delete this scheduled call?')) return;
    try {
      await api.delete(`${API}/zoho/scheduled-call/${callId}`);
      toast.success('Deleted');
      window.dispatchEvent(new Event('c30:calls-changed'));
      fetchMine();
      setTeamRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  // Today's scheduled calls → Complete flow; everything else → Edit in place.
  // Shared by both "My Calls" and "My Team" (the target summary decides
  // which bucket a given call id belongs to).
  const openCallFrom = (targetSummary) => (c) => {
    const status = (c.call_status || '').toLowerCase();
    const todayIds = new Set((targetSummary?.today || []).map(x => x.id));
    if (status === 'scheduled' && todayIds.has(c.id)) {
      setActiveCall(c);
    } else {
      setEditingCall(c);
    }
  };

  const headerRightButtons = (
    <>
      <button
        onClick={() => setShowScheduleModal(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-white"
        style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)' }}
        data-testid="manage-calls-header-schedule"
      >
        <CalendarClock className="w-3.5 h-3.5" /> Schedule
      </button>
      <button
        onClick={() => (tab === 'mine' ? fetchMine() : setTeamRefreshTick((t) => t + 1))}
        className="p-1.5 rounded-full bg-white/90 hover:bg-white transition-colors"
        data-testid="manage-calls-header-refresh"
        title="Refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 text-gray-700 ${loading && tab === 'mine' ? 'animate-spin' : ''}`} />
      </button>
    </>
  );

  const headerLeftContent = (
    <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
      <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Manage CRM</span>
    </div>
  );

  const mobileMenuButtons = [
    { icon: CalendarClock, label: 'Schedule Call', onClick: () => setShowScheduleModal(true), color: '#8b5cf6' },
    { icon: RefreshCw, label: 'Refresh', onClick: () => (tab === 'mine' ? fetchMine() : setTeamRefreshTick((t) => t + 1)), color: '#6b7280' },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Global header — identical shell to the Hub / other pages */}
      <AppHeader
        user={user}
        onLogout={onLogout}
        subHeaderLeft={headerLeftContent}
        subHeaderRight={headerRightButtons}
        mobileMenuButtons={mobileMenuButtons}
      />

      {/* Main content — top padding clears the fixed AppHeader */}
      <div className="pt-[20px] px-4 md:px-8 pb-10 bg-gray-100 min-h-screen">
        <div className="max-w-[1400px] mx-auto">
          {/* Title row — tab switcher sits on the right when manager view is
              available. The active tab gets the orange fill from the design
              spec in the attached view-switcher mock. */}
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight" data-testid="manage-calls-title">Manage CRM</h1>
              <p className="text-xs text-gray-500">Everything you've scheduled, completed, or missed</p>
            </div>
            {isManager && (
              <div className="flex bg-white rounded-full border border-gray-200 p-1 shadow-sm shrink-0">
                <button
                  onClick={() => setTab('mine')}
                  className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                    tab === 'mine' ? 'text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  style={tab === 'mine' ? { background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' } : undefined}
                  data-testid="manage-calls-tab-mine"
                >
                  My Calls
                </button>
                <button
                  onClick={() => setTab('team')}
                  className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 cursor-pointer ${
                    tab === 'team' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="manage-calls-tab-team"
                >
                  <Users2 className="w-4 h-4" /> My Team
                </button>
              </div>
            )}
          </div>

          {!sessionReady ? (
            <div className="rounded-2xl bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 p-8 text-center shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 mb-1">Sign in to Zoho CRM first</h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto mb-5">
                CRM features stay hidden until you've confirmed your Zoho session for this browser. It takes 10 seconds and you'll only see this prompt once per session.
              </p>
              <Button
                onClick={openPrompt}
                className="rounded-xl h-10 px-5 text-white font-semibold shadow-md"
                style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
                data-testid="manage-calls-sign-in-cta"
              >
                <ShieldCheck className="w-4 h-4 mr-2" /> Sign in to unlock
              </Button>
            </div>
          ) : tab === 'mine' ? (
            <CallsScopedView
              summary={summary}
              loading={loading}
              onOpenCall={openCallFrom(summary)}
              onDelete={handleDelete}
              showEightXEight
            />
          ) : (
            <MyTeamView
              onOpenCall={openCallFrom}
              onDelete={handleDelete}
              refreshTick={teamRefreshTick}
            />
          )}
        </div>
      </div>

      {/* Complete-scheduled modal (Today's scheduled calls) */}
      <AddCallToCrmModal
        isOpen={!!activeCall}
        onClose={() => setActiveCall(null)}
        scheduledCall={activeCall}
        onCompleted={() => { setActiveCall(null); fetchMine(); setTeamRefreshTick((t) => t + 1); }}
        onDeleted={() => { setActiveCall(null); fetchMine(); setTeamRefreshTick((t) => t + 1); }}
      />
      {/* Edit-in-place modal (any other call clicked on) */}
      <AddCallToCrmModal
        isOpen={!!editingCall}
        onClose={() => setEditingCall(null)}
        editCall={editingCall}
        onSaved={() => { setEditingCall(null); fetchMine(); setTeamRefreshTick((t) => t + 1); }}
        onDeleted={() => { setEditingCall(null); fetchMine(); setTeamRefreshTick((t) => t + 1); }}
      />
      <ScheduleCallModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onScheduled={() => { fetchMine(); setTeamRefreshTick((t) => t + 1); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatCard({ label, value, icon: Icon, colour }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-200 shadow-sm p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colour}20` }}>
        <Icon className="w-5 h-5" style={{ color: colour }} />
      </div>
      <div>
        <div className="text-2xl font-extrabold text-gray-900 leading-none">{value ?? 0}</div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mt-1">{label}</div>
      </div>
    </div>
  );
}

function CallRow({ call, onOpen, onDelete, showDelete = false }) {
  const status = (call.call_status || '').toLowerCase();
  const badgeColour = STATUS_COLOURS[status] || '#6b7280';
  // Status-driven card treatments so the cards pop out from the page bg.
  const cardBg = {
    scheduled: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',  // soft purple
    completed: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',  // soft green
    overdue:   'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',  // soft amber
  }[status] || 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)';

  return (
    <div
      className="group relative flex flex-col rounded-2xl shadow-sm hover:shadow-lg px-3 py-3 h-full transition-all cursor-pointer border-l-4 hover:-translate-y-0.5"
      style={{ background: cardBg, borderLeftColor: badgeColour }}
      onClick={() => onOpen && onOpen(call)}
      data-testid={`call-row-${call.id}`}
    >
      <div className="flex items-start gap-2 mb-1">
        <span className="font-bold text-sm text-gray-900 truncate flex-1" title={call.account?.name || ''}>
          {call.account?.name || '(no account)'}
        </span>
        <span
          className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white shrink-0 shadow-sm"
          style={{ backgroundColor: badgeColour }}
        >
          {call.call_status || '—'}
        </span>
      </div>
      <div className="text-[11px] text-gray-600 flex items-center gap-1.5 mb-1 font-semibold">
        <Clock className="w-3 h-3 shrink-0" />
        <span className="truncate">{_fmtDateTime(call.call_start_time)}</span>
      </div>
      {call.call_purpose && (
        <div className="text-[11px] text-gray-700 truncate mb-1 font-medium" title={call.call_purpose}>
          {call.call_purpose}
        </div>
      )}
      {call.description && !call.tag && (
        <div
          className="text-[11px] text-gray-600 italic mb-2 line-clamp-3"
          title={call.description}
        >
          {call.description}
        </div>
      )}
      {/* Action bar — anchored to the bottom of the card */}
      <div className="flex items-center gap-1.5 mt-auto pt-2 border-t border-white/70">
        {onOpen && (
          <Button
            onClick={(e) => { e.stopPropagation(); onOpen(call); }}
            size="sm"
            className="rounded-full h-7 px-3 text-[11px] flex-1 text-white font-semibold shadow-sm cursor-pointer"
            style={{ backgroundColor: badgeColour }}
            data-testid={`call-row-open-${call.id}`}
          >
            {status === 'scheduled' ? 'Start' : 'View'}
          </Button>
        )}
        {/* View in CRM link — shown for all logged calls that have a Zoho ID */}
        {call.id && (
          <a
            href={`https://crm.zoho.eu/crm/${process.env.REACT_APP_ZOHO_CRM_ORG || 'encon'}/tab/Calls/${call.id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="View in Zoho CRM"
            data-testid={`call-row-crm-link-${call.id}`}
            className="w-7 h-7 rounded-full bg-white/70 border border-white hover:border-blue-300 hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex items-center justify-center transition-colors shrink-0"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {showDelete && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(call.id); }}
            className="w-7 h-7 rounded-full bg-white/70 border border-white hover:border-red-300 hover:bg-red-50 text-gray-500 hover:text-red-600 flex items-center justify-center transition-colors shrink-0 cursor-pointer"
            title="Delete"
            data-testid={`call-row-delete-${call.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {/* Tag pill — shown at the very bottom when a tag exists */}
      {call.tag && (
        <div className="mt-1.5 flex justify-center">
          <span
            className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 cursor-default max-w-full truncate"
            title={call.description ? call.description : call.tag}
            data-testid={`call-row-tag-${call.id}`}
          >
            {call.tag}
          </span>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, count, suffix, headerAccent, children, empty = 'Nothing here yet.' }) {
  const accentClass = headerAccent === 'amber'
    ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'
    : '';
  const titleClass = headerAccent === 'amber' ? 'text-amber-900' : 'text-gray-900';
  return (
    <div className={`rounded-2xl bg-white border ${headerAccent === 'amber' ? 'border-amber-200' : 'border-gray-200'} shadow-sm mb-4`}>
      <div className={`flex items-center gap-2 px-4 py-3 border-b ${headerAccent === 'amber' ? 'border-amber-100' : 'border-gray-100'} ${accentClass}`}>
        <Icon className={`w-4 h-4 ${headerAccent === 'amber' ? 'text-amber-600' : 'text-gray-500'}`} />
        <h3 className={`text-sm font-bold uppercase tracking-wider ${titleClass}`}>{title}</h3>
        <span className={`ml-auto text-[11px] font-semibold flex items-center gap-2 ${headerAccent === 'amber' ? 'text-amber-700' : 'text-gray-500'}`}>
          <span>{count ?? 0}</span>
          {suffix}
        </span>
      </div>
      <div className="p-3">
        {(count ?? 0) === 0 ? (
          <div className="text-xs text-gray-400 italic px-2 py-3">{empty}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

// Day-tag used above the "All Scheduled" grouped view (e.g. "MON 28TH APR")
function DayTag({ date }) {
  const d = new Date(date);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase();
  const dayNum = d.getDate();
  const month = d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase();
  const suffix = (n) => (n % 100 >= 11 && n % 100 <= 13) ? 'TH' : (['TH', 'ST', 'ND', 'RD'][n % 10] || 'TH');
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-sm"
         style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)' }}>
      {day} {dayNum}{suffix(dayNum)} {month}
    </div>
  );
}

// Group a list of calls by yyyy-mm-dd (local time).
function groupByDay(calls) {
  const map = new Map();
  calls.forEach(c => {
    const dt = c.call_start_time ? new Date(c.call_start_time) : null;
    if (!dt) return;
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    if (!map.has(key)) map.set(key, { date: key, calls: [] });
    map.get(key).calls.push(c);
  });
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

// ---------------------------------------------------------------------------

/**
 * Scheduled Calls — single section with a Today / All Scheduled toggle.
 * "Today" always folds in overdue calls (they need chasing regardless of
 * the toggle); "All Scheduled" additionally groups in every upcoming call
 * by day, same grouped layout the old "Upcoming" section used.
 */
function ScheduledCallsSection({ summary, onOpenCall, onDelete }) {
  const [mode, setMode] = useState('today'); // 'today' | 'all'
  const [showFullAll, setShowFullAll] = useState(false);

  const overdueList = summary.overdue || [];
  const todayList = summary.today || [];
  const upcomingList = summary.upcoming || [];
  const todayCombined = [...overdueList, ...todayList];
  const allCombined = [...overdueList, ...todayList, ...upcomingList];

  const grouped = useMemo(() => groupByDay(allCombined), [allCombined]);
  const visibleCount = showFullAll ? allCombined.length : 5;
  let shown = 0;
  const visibleGroups = [];
  for (const g of grouped) {
    if (shown >= visibleCount) break;
    const take = g.calls.slice(0, Math.max(0, visibleCount - shown));
    visibleGroups.push({ date: g.date, calls: take });
    shown += take.length;
  }

  return (
    <div className="rounded-2xl bg-white border border-gray-200 shadow-sm mb-4" data-testid="scheduled-calls-section">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 flex-wrap">
        <CalendarClock className="w-4 h-4 text-gray-500" />
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Scheduled Calls</h3>
        {overdueList.length > 0 && (
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
            {overdueList.length} overdue
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5" data-testid="scheduled-calls-toggle">
          <button
            type="button"
            onClick={() => setMode('today')}
            data-testid="scheduled-calls-toggle-today"
            className={`px-3 h-7 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
              mode === 'today' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Today{todayCombined.length > 0 ? ` (${todayCombined.length})` : ''}
          </button>
          <button
            type="button"
            onClick={() => setMode('all')}
            data-testid="scheduled-calls-toggle-all"
            className={`px-3 h-7 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${
              mode === 'all' ? 'bg-white shadow-sm text-purple-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All Scheduled{allCombined.length > 0 ? ` (${allCombined.length})` : ''}
          </button>
        </div>
      </div>
      <div className="p-3">
        {mode === 'today' ? (
          todayCombined.length === 0 ? (
            <div className="text-xs text-gray-400 italic px-2 py-3">No calls scheduled for today.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {todayCombined.map(call => (
                <CallRow
                  key={call.id}
                  call={call}
                  onOpen={onOpenCall}
                  onDelete={onDelete}
                  showDelete={(call.call_status || '').toLowerCase() !== 'completed'}
                />
              ))}
            </div>
          )
        ) : allCombined.length === 0 ? (
          <div className="text-xs text-gray-400 italic px-2 py-3">Nothing scheduled.</div>
        ) : (
          <>
            {visibleGroups.map(g => (
              <div key={g.date} className="mb-4 last:mb-0">
                <div className="mb-2"><DayTag date={g.date} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {g.calls.map(call => (
                    <CallRow key={call.id} call={call} onOpen={onOpenCall} onDelete={onDelete} showDelete />
                  ))}
                </div>
              </div>
            ))}
            {allCombined.length > 5 && (
              <button
                onClick={() => setShowFullAll(v => !v)}
                className="w-full mt-2 rounded-xl border-2 border-dashed border-purple-200 hover:border-purple-400 bg-purple-50/40 hover:bg-purple-50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-purple-700 transition-colors cursor-pointer"
                data-testid="scheduled-all-toggle-btn"
              >
                {showFullAll ? 'Show fewer · Back to next 5' : `Show all ${allCombined.length} scheduled calls`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Inline "8x8 Calls" section — the same Calls Made / Calls Logged list from
// the notification bar's 8x8 modal, just embedded directly on the page.
function EightXEightSection() {
  const [callsMade, setCallsMade] = useState([]);
  const [callsLogged, setCallsLogged] = useState([]);

  const fetchCalls = useCallback(async () => {
    try {
      const res = await api.get(`${API}/8x8/active-calls`);
      setCallsMade(res.data.calls_made || []);
      setCallsLogged(res.data.calls_logged || []);
    } catch { /* best-effort skeleton feature */ }
  }, []);

  useEffect(() => {
    fetchCalls();
    const t = setInterval(fetchCalls, 20000);
    return () => clearInterval(t);
  }, [fetchCalls]);

  return (
    <div className="rounded-2xl bg-white border-2 shadow-sm mb-4" style={{ borderColor: EIGHT_X_EIGHT_LIME }} data-testid="eightx8-calls-section">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: EIGHT_X_EIGHT_LIME }}>
          <PhoneCall className="w-3.5 h-3.5" style={{ color: EIGHT_X_EIGHT_DARK }} />
        </span>
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: EIGHT_X_EIGHT_DARK }}>8x8 Calls</h3>
      </div>
      <div className="p-3">
        <EightByEightCallsList callsMade={callsMade} callsLogged={callsLogged} onCallLogged={fetchCalls} />
      </div>
    </div>
  );
}

// Shared page body — Stats → Scheduled Calls → (8x8 Calls) → Completed Calls.
// Used both by "My Calls" (own data) and "My Team" (a selected member's data).
function CallsScopedView({ summary, loading, onOpenCall, onDelete, showEightXEight = false }) {
  if (loading && !summary) {
    return <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-12"><Loader2 className="w-4 h-4 animate-spin" />Loading calls…</div>;
  }
  if (!summary) return null;
  if (!summary.zoho_user_id) {
    return <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-6 text-sm text-amber-800">This account isn't linked to a Zoho CRM user, so there are no calls to manage here. Ask an admin to add them to Zoho CRM.</div>;
  }
  const c = summary.counts || {};

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Today" value={c.today} icon={Phone} colour="#ee7203" />
        <StatCard label="Upcoming" value={c.upcoming} icon={CalendarClock} colour="#8b5cf6" />
        <StatCard label="Last 7 Days" value={c.last_7_days} icon={BarChart3} colour="#2563eb" />
        <StatCard label="Completed" value={c.completed} icon={CheckCircle2} colour="#10b981" />
        <StatCard label="Overdue" value={c.overdue} icon={AlertTriangle} colour="#f59e0b" />
      </div>

      <ScheduledCallsSection summary={summary} onOpenCall={onOpenCall} onDelete={onDelete} />

      {showEightXEight && <EightXEightSection />}

      <Section
        title="Completed Calls"
        icon={CheckCircle2}
        count={(summary.completed_last_7_days || []).length}
        suffix="last 7 days"
        empty="No completed calls in the last 7 days."
      >
        {(summary.completed_last_7_days || []).map(call => (
          <CallRow key={call.id} call={call} onOpen={onOpenCall} />
        ))}
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * "My Team" — same page as "My Calls" but scoped to one member at a time of
 * a team the manager runs (mirrors the Sales Hub's Team Hub member tabs).
 */
function MyTeamView({ onOpenCall, onDelete, refreshTick }) {
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [activeMemberId, setActiveMemberId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`${API}/teams`)
      .then(res => {
        const all = res.data?.teams || [];
        setTeams(all);
        if (all.length) setSelectedTeamId(all[0].id);
      })
      .catch(() => toast.error('Failed to load teams'))
      .finally(() => setTeamsLoading(false));
  }, []);

  const selectedTeam = teams.find(t => t.id === selectedTeamId) || null;
  const members = useMemo(() => {
    if (!selectedTeam) return [];
    return (selectedTeam.member_user_ids || []).map((id, i) => ({
      id, name: selectedTeam.member_names?.[i] || 'Unknown',
    }));
  }, [selectedTeam]);

  useEffect(() => {
    if (members.length && !members.some(m => m.id === activeMemberId)) {
      setActiveMemberId(members[0].id);
    }
  }, [members, activeMemberId]);

  const fetchMemberSummary = useCallback(async () => {
    if (!activeMemberId) return;
    setLoading(true);
    try {
      const res = await api.get(`${API}/zoho/my-calls/summary`, { params: { user_id: activeMemberId } });
      setSummary(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load this team member's calls");
    } finally {
      setLoading(false);
    }
  }, [activeMemberId]);

  useEffect(() => { fetchMemberSummary(); }, [fetchMemberSummary, refreshTick]);

  if (teamsLoading) {
    return <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-12"><Loader2 className="w-4 h-4 animate-spin" />Loading your team…</div>;
  }
  if (!teams.length) {
    return <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-6 text-sm text-amber-800">You don't manage any teams yet. Ask an admin to set one up in Team Management.</div>;
  }

  return (
    <>
      {teams.length > 1 && (
        <select
          value={selectedTeamId || ''}
          onChange={(e) => { setSelectedTeamId(e.target.value); setActiveMemberId(null); }}
          className="rounded-lg h-9 border-2 border-gray-300 px-2 text-sm bg-white mb-3"
          data-testid="my-team-team-select"
        >
          {teams.map(t => <option key={t.id} value={t.id}>{t.team_name}</option>)}
        </select>
      )}

      {members.length === 0 ? (
        <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-6 text-sm text-amber-800">This team has no members assigned yet.</div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1" data-testid="my-team-member-tabs">
            {members.map(m => (
              <button
                key={m.id}
                onClick={() => setActiveMemberId(m.id)}
                className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors cursor-pointer ${
                  activeMemberId === m.id ? 'text-white shadow-sm' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
                style={activeMemberId === m.id ? { background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)' } : undefined}
                data-testid={`my-team-member-tab-${m.id}`}
              >
                {m.name}
              </button>
            ))}
          </div>
          <CallsScopedView
            summary={summary}
            loading={loading}
            onOpenCall={onOpenCall(summary)}
            onDelete={onDelete}
            showEightXEight={false}
          />
        </>
      )}
    </>
  );
}
